/**
 * Platform Capability Probe
 *
 * One-shot, boot-time check for two iOS-specific reliability gaps this app
 * has to work around:
 *   - whether IndexedDB actually works (iOS Safari can leave indexedDB.open()
 *     unsettled forever, worst in installed-PWA mode)
 *   - whether the `dvh` viewport unit is supported (needs iOS 15.4+)
 *
 * Exists so a future bug report ("toast doesn't show on my phone") can be
 * diagnosed from one console line instead of re-deriving platform behaviour
 * from scratch in whichever code path happens to be affected that time.
 * See REPORT_iOS_TOAST_LAYOUT_2026-08-09.md for the investigation that found
 * both gaps.
 *
 * This module is diagnostic only. Requirements 1-3's fixes (getDB()'s own
 * timeout, the toast/IndexedDB decoupling, the dvh CSS fallback) do not
 * depend on this file existing — each holds on its own. This probe may later
 * be used as a fast-path short-circuit (skip a real IndexedDB call if
 * already known unhealthy), but is not that today, and must never replace
 * the per-call timeout in recordingStore.js, since health can change
 * mid-session (e.g. Safari reclaiming storage under memory pressure).
 */

/**
 * Result of the most recent probe. `null` fields mean "not yet probed".
 * Exported so a call site can read the current state synchronously without
 * awaiting probeCapabilities() again.
 */
export const caps = {
  indexedDbHealthy: null,
  dvhSupported: null,
};

/**
 * Run the probe once and log the combined result.
 *
 * Deliberately not awaited by callers that care about startup latency — see
 * main.js, where this is fired without `await` so it cannot delay the
 * loader's dismissal.
 *
 * @returns {Promise<typeof caps>}
 */
export async function probeCapabilities() {
  caps.dvhSupported = probeDvhSupport();
  caps.indexedDbHealthy = await probeIndexedDb();

  console.info(
    `📱 Platform capabilities: dvh=${caps.dvhSupported} indexedDb=${caps.indexedDbHealthy}`
  );

  if (typeof window !== 'undefined') {
    window.__kassieCaps = caps;
  }

  return caps;
}

/**
 * @returns {boolean}
 */
function probeDvhSupport() {
  return (
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('height', '100dvh')
  );
}

/**
 * Reuses recordingStore.js's own timeout-bounded getDB() rather than
 * re-implementing a second timeout here — one guard, not duplicated.
 *
 * @returns {Promise<boolean>}
 */
async function probeIndexedDb() {
  if (typeof indexedDB === 'undefined') return false;

  try {
    const { getDB } = await import('../services/recordingStore.js');
    const db = await getDB();
    return db !== null;
  } catch {
    return false;
  }
}
